/**
 * 新闻舱「怎么借这条爆款」(2026-10-10 §4 / 冻结契约 C3)。
 *
 * 修 P5:参考片内容**不再拼进 materialRaw**(ai fact_guard 把素材当证据 → 参考片数字能过事实校验),
 * 改由独立字段 `borrow` 上送;quotes/styleLines 在 ai 侧不算证据。
 * 修 P6:体裁不再恒 PRODUCT_COPY —— CITE→企业新闻,STYLE/STRUCTURE→按蓝图用途。
 *
 * 放 entities:features/explosive(建 preset 定默认体裁)与 features/reporter / pages/reporter
 * (页面切借法、拼请求体)都要用,features 互不 import(ADR-0013)。
 */
import { platformLabel } from "@/shared/lib/platformLabel";

import type { BlueprintLine } from "./blueprintLines";

export type BorrowMode = "CITE" | "STYLE" | "STRUCTURE";

export interface ReporterBorrowCraft {
  structure?: string;
  emotion?: string;
  copyFormula?: string;
  cta?: string;
}

/** C3 请求体 `borrow`(字段名冻结)。 */
export interface ReporterBorrow {
  mode: BorrowMode;
  quotes?: { text: string; attribution: string }[];
  styleLines?: string[];
  craft?: ReporterBorrowCraft;
  /** C1 逐句分析里全部参考片事实(去重):ai fact_guard 用它确定性认出参考片品牌/数字(黑名单,不是证据)。 */
  refFacts?: { span: string; kind: string }[];
}

/** 体裁(与 scene-card ReporterPresetGenre 的子集同值;只会落这三种)。 */
export type BorrowGenre = "COMPANY_NEWS" | "PRODUCT_COPY" | "BRAND_PROFILE";

export const BORROW_MODE_META: Record<
  BorrowMode,
  { label: string; desc: string; genreHint: string }
> = {
  CITE: {
    label: "借热度写",
    desc: "引用它的原话当由头,注明出处,正文讲你家",
    genreHint: "体裁:企业新闻",
  },
  STYLE: {
    label: "仿它的说法",
    desc: "标题/导语套它的句式,事实全换成你家的",
    genreHint: "体裁:按用途",
  },
  STRUCTURE: {
    label: "只学结构",
    desc: "原话不进稿,只学段落顺序和基调",
    genreHint: "体裁:按用途",
  },
};

/**
 * 体裁:CITE → COMPANY_NEWS;STYLE/STRUCTURE → 按 `payload.purposes`
 * (含「产品带货」→ PRODUCT_COPY,含品牌类 → BRAND_PROFILE,其余 → COMPANY_NEWS)。
 * purposes 是 ai 侧自由形状,非字符串数组一律当缺省。
 */
export function genreForBorrow(mode: BorrowMode, purposes: unknown): BorrowGenre {
  if (mode === "CITE") return "COMPANY_NEWS";
  const list = Array.isArray(purposes)
    ? purposes.filter((p): p is string => typeof p === "string")
    : [];
  if (list.some((p) => p.includes("产品带货"))) return "PRODUCT_COPY";
  if (list.some((p) => p.includes("品牌"))) return "BRAND_PROFILE";
  return "COMPANY_NEWS";
}

/** 默认出处:「据{平台中文名}一则热门视频」;平台未知不编名字。 */
export function defaultAttribution(platform: string | null | undefined): string {
  const label = platformLabel(platform);
  return label ? `据${label}一则热门视频` : "据一则热门视频";
}

/** 默认勾选:钩子句(全部);没有钩子句 → 第一句。返回句子原文(选择以文本记,草稿回显也按文本对)。 */
// 1010 演示机真点:一条片常被拆出 4-5 句 hook(含参考片品牌),全勾 = 引用 5 句 / 仿 5 句,不像话;
// 默认只勾第一句钩子,要多勾用户自己点。
export function defaultBorrowPicks(lines: BlueprintLine[]): string[] {
  const hook = lines.find((l) => l.role === "hook") ?? lines[0];
  return hook ? [hook.text] : [];
}

function nonBlank(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() ? v.trim() : undefined;
}

/** craft(三档都带,去产品化公式):payload.copyFormula/cta + breakdown.structure/emotion;全空 → undefined。 */
export function borrowCraftOf(
  payload: { copyFormula?: unknown; cta?: unknown } | null | undefined,
  breakdown: { structure?: unknown; emotion?: unknown } | null | undefined
): ReporterBorrowCraft | undefined {
  const craft: ReporterBorrowCraft = {};
  const structure = nonBlank(breakdown?.structure);
  const emotion = nonBlank(breakdown?.emotion);
  const copyFormula = nonBlank(payload?.copyFormula);
  const cta = nonBlank(payload?.cta);
  if (copyFormula) craft.copyFormula = copyFormula;
  if (cta) craft.cta = cta;
  if (structure) craft.structure = structure;
  if (emotion) craft.emotion = emotion;
  return Object.keys(craft).length ? craft : undefined;
}

/** 拼 C3 `borrow`:按 mode 只带该档的字段(别的档的勾选态留在页面里,不上送)。 */
export function buildReporterBorrow(input: {
  mode: BorrowMode;
  quotes: string[];
  attribution: string;
  defaultAttr: string;
  styleLines: string[];
  craft: ReporterBorrowCraft | undefined;
  /** C1 逐句;带上它的 refFacts(三档都带 —— STRUCTURE 下品牌同样不该进稿)。 */
  lines?: BlueprintLine[];
}): ReporterBorrow {
  const out: ReporterBorrow = { mode: input.mode };
  if (input.mode === "CITE") {
    const attribution = input.attribution.trim() || input.defaultAttr;
    out.quotes = input.quotes.map((text) => ({ text, attribution }));
  } else if (input.mode === "STYLE") {
    out.styleLines = [...input.styleLines];
  }
  if (input.craft) out.craft = input.craft;
  const refFacts = refFactsOf(input.lines ?? []);
  if (refFacts.length) out.refFacts = refFacts;
  return out;
}

/** 逐句 refFacts 去重拍平(上限 40,与 api 校验同值)。 */
export function refFactsOf(lines: BlueprintLine[]): { span: string; kind: string }[] {
  const seen = new Set<string>();
  const out: { span: string; kind: string }[] = [];
  for (const l of lines) {
    for (const f of l.refFacts ?? []) {
      const span = f.span.trim();
      const key = `${f.kind}:${span}`;
      if (!span || span.length > 60 || seen.has(key)) continue;
      seen.add(key);
      out.push({ span, kind: f.kind });
      if (out.length >= 40) return out;
    }
  }
  return out;
}

/** GET 回来的 borrow 验型(null/脏 → null)。 */
export function parseReporterBorrow(raw: unknown): ReporterBorrow | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (o.mode !== "CITE" && o.mode !== "STYLE" && o.mode !== "STRUCTURE") return null;
  const out: ReporterBorrow = { mode: o.mode };
  if (Array.isArray(o.quotes)) {
    out.quotes = o.quotes
      .filter(
        (q): q is { text: string; attribution?: unknown } =>
          !!q && typeof q === "object" && typeof (q as { text?: unknown }).text === "string"
      )
      .map((q) => ({
        text: q.text,
        attribution: typeof q.attribution === "string" ? q.attribution : "",
      }));
  }
  if (Array.isArray(o.styleLines)) {
    out.styleLines = o.styleLines.filter((s): s is string => typeof s === "string");
  }
  if (Array.isArray(o.refFacts)) {
    const facts = o.refFacts.filter(
      (f): f is { span: string; kind: string } =>
        !!f &&
        typeof f === "object" &&
        typeof (f as { span?: unknown }).span === "string" &&
        typeof (f as { kind?: unknown }).kind === "string"
    );
    if (facts.length) out.refFacts = facts.map((f) => ({ span: f.span, kind: f.kind }));
  }
  if (o.craft && typeof o.craft === "object") {
    const c = borrowCraftOf(
      o.craft as { copyFormula?: unknown; cta?: unknown },
      o.craft as { structure?: unknown; emotion?: unknown }
    );
    if (c) out.craft = c;
  }
  return out;
}

/**
 * 1010 之前的草稿:写法段(含参考片原话)被拼在 materialRaw 最前面上送 ——
 * 「【写法参考】…」开头、空行分隔,后面才是用户素材。新版不再拼,但老草稿打开后这段还躺在素材框里,
 * 重新生成时它会被当成「用户素材」(= fact_guard 证据)送出去。识别出来给用户一键移除。
 * 不是这个开头 → craft=null 原样返回(不猜、不强摘)。
 */
export function splitLegacyCraft(raw: string): { craft: string | null; rest: string } {
  if (!raw.startsWith("【写法参考】")) return { craft: null, rest: raw };
  const cut = raw.indexOf("\n\n");
  if (cut < 0) return { craft: raw, rest: "" };
  return { craft: raw.slice(0, cut), rest: raw.slice(cut + 2) };
}
